/**
 * The item card's own toast (parity card **P-164**).
 *
 * Every destructive action the card offers — *Delete*, *Archive*, *Move to board*, *Move to
 * group*, *Duplicate* and the rename — used to complete in silence, while the identical action
 * taken from the grid's row menu raised a 10 s toast with a working **Undo** (round 9y). The
 * card is not mounted inside `TableViewProvider` (`routes/screens.tsx` renders it as a sibling
 * of the grid), so `useTable`'s toast is out of reach and `dismissToast` / `undoLast` are not
 * its to call.
 *
 * So the card gets its own host, deliberately rendering the **same surface** as the grid's —
 * `.ez-grid-toast` with `data-testid="grid-toast"` and `grid-toast-action` — because it is the
 * same product affordance in the same place on screen, and a person who has just archived an
 * item should not have to learn a second toast. Only one of the two is ever raised for one
 * action: the grid toasts what the grid did, the card toasts what the card did.
 *
 * The undo is the **inverse call**, exactly as the grid's undo entries are: `items.restore` for
 * a delete, `items.unarchive` for an archive, `items.move` back to the previous group for a
 * move, `items.rename` back to the previous name for a rename. It is re-issued through the same
 * service procedures, so it writes the same activity rows and emits the same events.
 */
import { useCallback, useEffect, useSyncExternalStore } from 'react';
// The host is mounted by the shell, which may never have rendered a card, so it brings its own
// stylesheet rather than relying on the card having loaded one.
import './item-card.css';

/** The grid's own deadline (`board-view/table/undo.ts#UNDO_TOAST_MS`), kept in step by hand. */
export const CARD_TOAST_MS = 10_000;

export interface CardToastEntry {
  /** A monotonic id, so a second action replaces the first toast rather than racing its timer. */
  id: number;
  message: string;
  kind: 'info' | 'error';
  actionLabel?: string;
  onAction?: () => void | Promise<void>;
}

export type RaiseCardToast = (entry: Omit<CardToastEntry, 'id'>) => void;

/**
 * **The store is module-level, and the host is mounted by the shell**, because half of what the
 * card toasts is an action that *closes the card*: Archive, Delete and Move to board all end
 * with `onGone`, and a toast rendered inside the panel was unmounted in the same commit that
 * raised it — the user saw nothing at all, which is the defect P-164 filed, one layer deeper.
 */
let toast: CardToastEntry | null = null;
let sequence = 0;
const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export const raiseCardToast: RaiseCardToast = (entry) => {
  sequence += 1;
  toast = { ...entry, id: sequence };
  emit();
};

export function dismissCardToast(id?: number) {
  if (id !== undefined && toast?.id !== id) return;
  toast = null;
  emit();
}

/** Test-only: drop whatever is on screen. */
export function __resetCardToast() {
  toast = null;
  sequence = 0;
  emit();
}

function useCardToastValue(): CardToastEntry | null {
  return useSyncExternalStore(
    subscribe,
    () => toast,
    () => null,
  );
}

/**
 * The host. One per app, mounted by `AppShell` beside the shell's other overlay hosts, so a
 * toast outlives the card that raised it and lands where the grid's own toast lands.
 */
export function CardToastHost() {
  const current = useCardToastValue();
  const dismiss = useCallback(() => dismissCardToast(current?.id), [current?.id]);

  useEffect(() => {
    if (!current) return undefined;
    const timer = setTimeout(() => dismissCardToast(current.id), CARD_TOAST_MS);
    return () => clearTimeout(timer);
  }, [current]);

  if (!current) return null;

  return (
    <div className="ez-grid-toast ez-item-card__toast" role="status" data-kind={current.kind} data-testid="grid-toast">
      <span>{current.message}</span>
      {current.actionLabel && current.onAction ? (
        <button
          type="button"
          className="ez-grid-toast__action"
          data-testid="grid-toast-action"
          onClick={() => {
            const run = current.onAction;
            dismiss();
            void run?.();
          }}
        >
          {current.actionLabel}
        </button>
      ) : null}
      <button type="button" className="ez-grid-toast__close" aria-label="Dismiss" data-testid="grid-toast-close" onClick={dismiss}>
        ×
      </button>
    </div>
  );
}
